import { useNavigate } from "react-router-dom";
import { useState } from "react";
import { useLanguage } from "@/contexts/LanguageContext";
import { ArrowRight, Sparkles, Play } from "lucide-react";
import { FloatingDashboard } from "./FloatingDashboard";
import { KSANetworkMap } from "./KSANetworkMap";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { DemoVideoPlayer } from "./DemoVideoPlayer";

interface HeroSectionProps {
  isVisible: boolean;
}

export const HeroSection = ({ isVisible }: HeroSectionProps) => {
  const navigate = useNavigate();
  const { t } = useLanguage();
  const [demoOpen, setDemoOpen] = useState(false);

  return (
    <section className="relative pt-6 md:pt-16 pb-10 md:pb-20">
      <div
        className={`grid lg:grid-cols-12 gap-8 lg:gap-10 items-center transition-all duration-1000 ${
          isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"
        }`}
      >
        {/* Left: copy */}
        <div className="lg:col-span-6">
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-primary/10 border border-primary/20 text-primary text-[10px] md:text-xs font-semibold tracking-widest uppercase mb-4 md:mb-6">
            <Sparkles className="w-3.5 h-3.5 shrink-0" />
            <span>AI Powered Advertising Platform</span>
          </div>

          <h1 className="font-bold leading-[1.08] md:leading-[1.05] tracking-tight mb-4 md:mb-6 text-[clamp(2rem,8.5vw,4.75rem)] text-balance">
            <span className="block text-foreground">{t("heroTitle") || "The Future of"}</span>
            <span className="block">
              <span className="bg-gradient-to-r from-primary to-primary-light bg-clip-text text-transparent">
                Smart
              </span>{" "}
              <span className="text-foreground">Advertising</span>
            </span>
          </h1>

          <p className="text-[15px] md:text-lg text-muted-foreground max-w-xl mb-6 md:mb-8 leading-relaxed">
            AI-powered platform for managing, buying, optimizing, and automating digital advertising
            across Saudi Arabia's largest media networks.
          </p>

          <div className="flex flex-col sm:flex-row sm:flex-wrap items-stretch sm:items-center gap-3 mb-8">
            <button
              onClick={() => navigate("/campaign")}
              className="h-12 px-7 rounded-full font-semibold text-base inline-flex items-center justify-center gap-2 group bg-gradient-to-r from-primary to-primary-light text-primary-foreground shadow-lg shadow-primary/30 hover:shadow-xl hover:shadow-primary/40 hover:-translate-y-0.5 transition-all"
            >
              {t("startCampaign") || "Launch Campaign"}
              <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-0.5" />
            </button>
            <button
              onClick={() => setDemoOpen(true)}
              className="h-12 px-6 rounded-full font-semibold text-base inline-flex items-center justify-center gap-2 bg-surface border border-border text-foreground hover:bg-surface-muted transition-colors shadow-card group"
            >
              <Play className="w-4 h-4 text-primary transition-transform group-hover:scale-110" fill="currentColor" />
              Watch Demo
            </button>
          </div>


        </div>

        {/* Right: floating dashboard composition */}
        <div className="lg:col-span-6 relative">
          <div
            className="absolute -inset-8 -z-10 opacity-60"
            style={{
              background:
                "radial-gradient(60% 60% at 60% 40%, hsl(var(--primary) / 0.18), transparent 70%)",
            }}
          />
          <div className="relative">
            <div className="relative z-10 -rotate-1 hover:rotate-0 transition-transform duration-500">
              <FloatingDashboard />
            </div>
            <div className="hidden md:block absolute -right-4 -bottom-6 w-[60%] z-20 rotate-2 hover:rotate-0 transition-transform duration-500">
              <div className="rounded-2xl bg-gradient-to-br from-[hsl(220_60%_10%)] to-[hsl(220_55%_14%)] border border-white/10 shadow-2xl shadow-primary/30 p-4">
                <div className="flex items-center justify-between mb-2">
                  <div className="text-[10px] font-semibold text-white/70 uppercase tracking-widest">
                    Live Network
                  </div>
                  <span className="px-2 py-0.5 rounded-full bg-emerald-500/20 text-emerald-300 text-[9px] font-semibold">
                    Online
                  </span>
                </div>
                <div className="text-3xl font-bold text-white leading-none mb-1">4,500+</div>
                <div className="text-[10px] text-white/50 mb-3">Active Faces</div>
                <div className="rounded-xl overflow-hidden bg-[hsl(220_60%_4%)]/60 p-2">
                  <KSANetworkMap width={420} height={220} showLabels={false} />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <Dialog open={demoOpen} onOpenChange={setDemoOpen}>
        <DialogContent className="max-w-6xl p-0 overflow-hidden border-0 bg-transparent shadow-none">
          <DialogTitle className="sr-only">Fusion Media platform demo</DialogTitle>
          <div
            className="relative w-full overflow-hidden rounded-2xl bg-black ring-1 ring-white/10"
            style={{
              aspectRatio: "16 / 9",
              boxShadow:
                "0 40px 120px -20px rgba(59,130,246,0.45), 0 0 0 1px rgba(255,255,255,0.06) inset",
            }}
          >
            {demoOpen && <DemoVideoPlayer src="/videos/demo.mp4" />}
          </div>
        </DialogContent>
      </Dialog>
    </section>
  );
};
